import type { FileRecord } from "@cohesive/api-client";
import {
  extractReferencedFiles,
  fileRecordVersionKey,
  useFileRecordText,
} from "@cohesive/ui-components";
import { useMemo } from "react";

// The public-URL regex and extraction live in @cohesive/ui-components
// (lib/htmlPublicUrls.ts, alongside their tests) — the single source shared
// with useHtmlPresignRewrite. Re-exported here so pane-side consumers keep
// their import path without this package carrying a hand-synced copy.
export {
  extractReferencedFiles,
  PUBLIC_URL_REGEX,
} from "@cohesive/ui-components";
export type { ReferencedFile } from "@cohesive/ui-components";

export interface UseHtmlReferencedIdsOptions {
  /**
   * When false, the body is not read and there is no data. Used by
   * `FilePublishButton` to skip the body fetch entirely for non-HTML kinds.
   * Defaults to true.
   */
  enabled?: boolean;
}

/**
 * The rendered HTML body and the referenced `file.{env}.cohesive.ai/public/{id}`
 * files found in it (each as an `{ id, url }` pair, plus a `referencedIds`
 * convenience list). The body comes from `useFileRecordText`, so the iframe
 * renderer and the publish dialog share one read per file version, always
 * from the latest link; a read that failed on an expired link is asked again
 * when a fresh one lands. Read failures are reported by the query cache.
 */
export function useHtmlReferencedIds(
  record: Pick<FileRecord, "url" | "path" | "content_hash" | "updated_at">,
  options: UseHtmlReferencedIdsOptions = {},
) {
  const { enabled = true } = options;
  const query = useFileRecordText({
    url: record.url,
    versionKey: fileRecordVersionKey(record),
    enabled,
  });

  // While a new version loads, `useFileRecordText` hands back the previous
  // version's body as a placeholder. The viewer pairs the body with the live
  // `content_hash`, so an old body against the new hash would be committed
  // as the new fingerprint and gate out the real update; stay empty instead.
  const body = query.isPlaceholderData ? undefined : query.data;
  const data = useMemo(() => {
    if (body === undefined) return undefined;
    const referencedFiles = extractReferencedFiles(body);
    return {
      body,
      referencedFiles,
      referencedIds: referencedFiles.map((f) => f.id),
    };
  }, [body]);

  return {
    data,
    isLoading: data === undefined && query.isFetching,
    isError: query.isError,
  };
}
